import React, { useState, useEffect } from 'react';
import { authService, User, Tenant } from '../utils/auth.ts';
import Timeline from '../components/Timeline.tsx';
import PhotoUpload from '../components/PhotoUpload.tsx';
import HamburgerMenu from '../components/HamburgerMenu.tsx';
import ProfileEditModal from '../components/ProfileEditModal.tsx';
import '../styles/colors.css';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AddIcon from '@mui/icons-material/Add';

const Home: React.FC = () => {
  const [user, setUser] = useState<User | null>(authService.getCurrentUser());
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleProfileUpdate = (updatedUser: User) => {
    setUser(updatedUser);
  };

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const tenantInfo = await authService.getTenantInfo();
        setTenant(tenantInfo);
      } catch (error) {
        console.error('Failed to fetch tenant info:', error);
      }
    };

    fetchTenantInfo();
  }, []);

  const handleUploadSuccess = () => {
    setShowUpload(false);
    setRefreshKey(prev => prev + 1); // タイムラインを再読み込み
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--neutral-100)' }}>
      {/* ヘッダー */}
      <header
        style={{
          background: 'var(--gradient-primary)',
          padding: '0.5rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto',
          }}
        >
          <h1
            style={{
              margin: 0,
              color: 'var(--neutral-50)',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              letterSpacing: '0.5px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CameraAltIcon
                style={{ marginRight: '8px', alignItems: 'center' }}
              />
              <div>
                <div
                  style={{ fontSize: '0.7rem', opacity: 0.9, lineHeight: 1 }}
                >
                  {tenant?.name || 'グループ'}
                </div>
                <div style={{ lineHeight: 1.2 }}>みといて</div>
              </div>
            </div>
          </h1>
          <HamburgerMenu
            user={user}
            onProfileEdit={() => setShowProfileEdit(true)}
          />
        </div>
      </header>

      {/* メインコンテンツ */}
      <main
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          paddingBottom: '5rem', // フローティングボタンのスペース確保
        }}
      >
        <Timeline key={refreshKey} />
      </main>

      {/* フローティングアクションボタン */}
      <button
        onClick={() => setShowUpload(true)}
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          width: '3rem',
          height: '3rem',
          borderRadius: '50%',
          border: 'none',
          background:
            'var(--gradient-accent, linear-gradient(135deg, #8b5cf6 0%, #2563eb 100%))',
          color: 'var(--neutral-50)',
          fontSize: '1.5rem',
          cursor: 'pointer',
          boxShadow:
            '0 4px 12px -2px rgb(0 0 0 / 0.1), 0 2px 6px -1px rgb(0 0 0 / 0.05)',
          transition: 'all 0.2s ease-in-out',
          zIndex: 1000,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)';
          e.currentTarget.style.boxShadow =
            '0 8px 20px -4px rgb(0 0 0 / 0.15), 0 4px 12px -2px rgb(0 0 0 / 0.1)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1) translateY(0)';
          e.currentTarget.style.boxShadow =
            '0 4px 12px -2px rgb(0 0 0 / 0.1), 0 2px 6px -1px rgb(0 0 0 / 0.05)';
        }}
      >
        <AddIcon />
      </button>

      {/* 写真投稿モーダル */}
      {showUpload && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--overlay-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem',
          }}
          onClick={e => {
            if (e.target === e.currentTarget) {
              setShowUpload(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: 'var(--radius-xl, 0.75rem)',
              boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgb(0 0 0 / 0.1))',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <PhotoUpload
              onUploadSuccess={handleUploadSuccess}
              onCancel={() => setShowUpload(false)}
            />
          </div>
        </div>
      )}

      {/* プロフィール編集モーダル */}
      {showProfileEdit && (
        <ProfileEditModal
          user={user}
          onClose={() => setShowProfileEdit(false)}
          onProfileUpdate={handleProfileUpdate}
        />
      )}
    </div>
  );
};

export default Home;
